// src/analytics/firebase.ts
//
// Firebase Web initialization for the Gather landing site.
//
// IMPORTANT: This registers a *Web App* inside your EXISTING Firebase project
// (the same project that has your iOS + Android apps). That means web events
// land in the SAME GA4 property as the app — great for an end-to-end funnel,
// but you'll want to filter by platform in reports (web events carry
// platform = "web" automatically).
//
// Where to get the config below:
//   Firebase Console → Project settings → General → "Your apps"
//   → Add app → Web (</>) → register "Gather Web" → copy the firebaseConfig.
//   The measurementId (G-XXXXXXX) is REQUIRED for Analytics — make sure it's there.
//
// CRA reads env vars prefixed with REACT_APP_ at build time. Put the values in
// a .env file (see .env.example) so you don't hardcode keys in the repo.

import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAnalytics,
  isSupported,
  setAnalyticsCollectionEnabled,
  type Analytics,
} from "firebase/analytics";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FB_API_KEY,
  authDomain: process.env.REACT_APP_FB_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FB_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FB_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FB_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FB_APP_ID,
  measurementId: process.env.REACT_APP_FB_MEASUREMENT_ID,
};

// Only collect on the real production host. This mirrors the RN app's
// `getCurrentEnvironment() === "production" && !__DEV__` gate: localhost and
// preview builds must NOT pollute the ad-test data.
const isProdHost = (): boolean =>
  typeof window !== "undefined" &&
  window.location.hostname === "gather-community.com";

let app: FirebaseApp | null = null;
let analyticsInstance: Analytics | null = null;
let initPromise: Promise<Analytics | null> | null = null;

// getAnalytics() can throw in unsupported environments (some in-app browsers,
// SSR, private modes). isSupported() guards against that. Returns null when
// analytics shouldn't or can't run — callers must handle null.
export const initAnalytics = (): Promise<Analytics | null> => {
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      if (!isProdHost()) return null;
      if (!firebaseConfig.measurementId) return null; // no GA4 wiring → bail
      if (!(await isSupported())) return null;

      app = initializeApp(firebaseConfig);
      analyticsInstance = getAnalytics(app);
      setAnalyticsCollectionEnabled(analyticsInstance, true);
      return analyticsInstance;
    } catch {
      // Never let analytics setup break the page.
      return null;
    }
  })();

  return initPromise;
};

export const getAnalyticsInstance = (): Analytics | null => analyticsInstance;
